import { purify } from '@vfit/shared/themes';
import { isDisabled, renderDescription } from './stickyOffer.utils';
import { Arrow } from '@vfit/shared-icons';
import * as S from './stickyOffer.style';
import { Button, ScrollableIndicator } from '@vfit/shared/atoms';
import { useMemo } from 'react';
import { IStickyOfferComponent } from './stickyOffer.model';

export const StickyOfferComponent = ({
  title,
  id,
  topLabel,
  isLoading,
  isLoadingGoNext,
  description,
  enableLiteUI,
  content,
  secondCta,
  ctaActivation,
  showDetails,
  setShowDetails,
  toggleDetails,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  showDetailLabel,
  hideDetailLabel,
  ctaContainerRef,
  ctaContainerWidth,
  activationBoxHeight,
  activationBoxRef,
  tmpSlideBanner,
  withProgressbar,
  stepProgress,
  extraClass,
  isApp,
  isDesktop,
  isPulsing,
}: IStickyOfferComponent) => {
  const [pTitle, pTopLabel] = purify([title, topLabel]);

  const renderShowDetail = () => {
    const desc = renderDescription(description, showDetails);
    if (!desc) return;
    return (
      <S.ShowDetailsCta
        rotation={showDetails === 'slide-up' ? '-90' : '90'}
        onClick={toggleDetails}
        data-name={`action_StickyOffer_details`}
      >
        <span>{desc}</span>
        <Arrow />
      </S.ShowDetailsCta>
    );
  };

  const renderActions = () => {
    if (enableLiteUI) {
      if (!isDesktop) return renderShowDetail();
      if (secondCta?.label) {
        return (
          <div
            style={{
              marginRight: 16,
            }}
          >
            <Button
              variant={'secondary'}
              onClick={() => {
                if (isLoadingGoNext) return;
                secondCta?.action?.();
              }}
              name={`action_StickyOffer_operatore`}
            >
              {secondCta.label}
            </Button>
          </div>
        );
      }
      return null;
    }
    return (
      <>
        {content && !isDesktop && renderShowDetail()}
        {content && isDesktop && (
          <div style={{ marginRight: '16px' }}>
            <Button onClick={toggleDetails} variant={'secondary'}>
              {showDetails !== 'slide-up'
                ? showDetailLabel || 'SCOPRI DETTAGLI'
                : hideDetailLabel || 'NASCONDI DETTAGLI'}
            </Button>
          </div>
        )}
      </>
    );
  };

  const DetailsController = useMemo(() => {
    if (isLoading) {
      return <S.DetailsLoading />;
    }

    return (
      <S.Details isApp={isApp} isPulsing={isPulsing}>
        <div className={extraClass}>
          {pTopLabel && (
            <S.TopLabel
              className="topLabel"
              dangerouslySetInnerHTML={{
                __html: pTopLabel,
              }}
            />
          )}
          {pTitle && (
            <p
              dangerouslySetInnerHTML={{
                __html: pTitle,
              }}
            />
          )}
          {enableLiteUI && isDesktop && renderShowDetail()}
        </div>
        {renderActions()}
      </S.Details>
    );
  }, [content, description, isDesktop, isLoading, showDetails, title, toggleDetails, isPulsing]);

  return (
    <>
      <S.OpaqueBackground
        id="opaque-background"
        isActive={!isApp ? '' : showDetails}
        onClick={() => setShowDetails('slide-down')}
      />
      <S.MainContainer
        id={id}
        isApp={isApp}
        activationBoxHeight={activationBoxHeight}
        className={`${showDetails} ${tmpSlideBanner || ''}`}
      >
        <S.ActivationBox
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          ref={activationBoxRef}
          withProgressbar={withProgressbar?.show}
        >
          {withProgressbar?.show && showDetails !== 'slide-up' && (
            <S.ProgressbarContainer>
              <S.Bar stepCoverage={stepProgress} />
            </S.ProgressbarContainer>
          )}
          {isApp && <ScrollableIndicator />}
          {DetailsController}
          <S.CtaContainer ref={ctaContainerRef} id="sticky-offer-next">
            {ctaActivation?.label && (
              <Button
                loading={isLoadingGoNext}
                disabled={isDisabled({
                  isLoadingGoNext,
                  variant: ctaActivation?.variant,
                  isDisabled: ctaActivation?.isDisabled,
                  isLoading,
                })}
                onClick={() => {
                  if (isLoadingGoNext) return;
                  if (showDetails === 'slide-up') setShowDetails('slide-down');
                  // actionClick is inhibited to avoid double url opening
                  !ctaActivation?.actionNavigationUrl && ctaActivation?.action?.();
                }}
                href={ctaActivation?.actionNavigationUrl}
                target={
                  ctaActivation?.actionNavigationUrl && ctaActivation?.isBlank === 'true'
                    ? '_blank'
                    : undefined
                }
                name={ctaActivation?.type ? `action_StickyOffer_${ctaActivation?.type}` : undefined}
                variant={
                  isApp
                    ? ctaActivation?.variant || 'mva:primary'
                    : ctaActivation?.variant || 'primary'
                }
              >
                {ctaActivation?.label}
              </Button>
            )}
          </S.CtaContainer>
        </S.ActivationBox>
        {content && showDetails === 'slide-up' && (
          <S.ChildrenContainer
            activationBoxHeight={activationBoxHeight}
            ctaContainerWidth={ctaContainerWidth}
          >
            <S.OverlayGlobalStyle />
            {content}
          </S.ChildrenContainer>
        )}
      </S.MainContainer>
    </>
  );
};

